import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import DatePickerNewLedger from "../../assets/DatePickerNewLedger.svg";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import {
  deleteButtonStyles,
  dialogStyles,
  labelTypography,
  saveButtonStyles,
  typographyStyles,
} from "./AddNewLedger.styles";
import { updateLedger, deleteLedger } from "../../api/ledgerAPI";

interface Entry {
  id: string | undefined;
  date: string;
  orderId: string;
  creditAmount: string;
}
interface AddNewEntryModalProps {
  open: boolean;
  onClose: () => void;
  entry?: Entry | null;
  fetchLedgerData: (page: string, limit: string) => Promise<void>;
  customerId: string;
}

export const AddNewLedger: React.FC<AddNewEntryModalProps> = ({
  open,
  onClose,
  entry,
  fetchLedgerData,
  customerId,
}) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(
    entry ? moment(entry.date) : null
  );
  const [orderId, setOrderId] = useState(entry?.orderId || "");
  const [creditAmount, setCreditAmount] = useState(entry?.creditAmount || "");
  const resetForm = () => {
    setSelectedDate(null);
    setOrderId("");
    setCreditAmount("");
  };
  useEffect(() => {
    if (!entry) {
      resetForm();
    } else {
      const parsedDate = moment(entry.date, "YYYY-MM-DD");
      setSelectedDate(parsedDate);
      setOrderId(entry.orderId);
      setCreditAmount(entry.creditAmount);
    }
  }, [entry]);

  const handleSave = async () => {
    if (selectedDate && creditAmount) {
      const payload = {
        id: entry ? entry.id : undefined,
        customerId,
        createdAt: selectedDate.format("YYYY-MM-DD"),
        orderId,
        credit: creditAmount,
      };

      try {
        await updateLedger(payload);
        resetForm(); // <-- Add this line

        onClose();
        fetchLedgerData("1", "10");
      } catch (error) {
        console.error("Error saving ledger entry:", error);
      }
    }
  };

  const handleDelete = async () => {
    if (entry?.id) {
      try {
        await deleteLedger(entry.id);
        onClose();
        fetchLedgerData("1", "10");
      } catch (error) {
        console.error("Error deleting ledger entry:", error);
      }
    }
  };
  const handleClose = () => {
    resetForm();
    onClose();
  };
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        sx={dialogStyles}
      >
        <DialogTitle sx={{ marginBottom: "20px" }}>
          <Typography variant="h6" sx={typographyStyles}>
            {entry ? "Edit Entry" : "Add New Entry"}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Choose Date */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" sx={labelTypography}>
                Choose Date
              </Typography>
              <MuiDatePicker
                value={selectedDate}
                onChange={(newDate) => setSelectedDate(newDate)}
                format="DD/MM/YYYY"
                maxDate={moment()}
                disabled={!!entry}
                open={isDatePickerOpen}
                onOpen={() => !entry && setIsDatePickerOpen(true)}
                onClose={() => setIsDatePickerOpen(false)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: "Select date",
                    InputProps: {
                      endAdornment: (
                        <IconButton
                          onClick={() => !entry && setIsDatePickerOpen(true)}
                          disabled={!!entry}
                        >
                          <img
                            src={DatePickerNewLedger}
                            alt="Calendar Icon"
                            width={24}
                            height={24}
                            style={{ opacity: entry ? 0.5 : 1 }}
                          />
                        </IconButton>
                      ),
                    },
                  },
                }}
              />
            </Grid>

            {/* Order ID */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" sx={labelTypography}>
                Order ID
              </Typography>
              <TextField
                data-testid="OrderID Field"
                fullWidth
                value={orderId}
                disabled={!!entry}
                onChange={(e) => setOrderId(e.target.value)}
              />
            </Grid>

            {/* Credit Amount */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  fontFamily: "Roboto",
                  paddingBottom: "8px",
                  paddingTop: "20px",
                }}
              >
                Credit Amount
              </Typography>
              <TextField
                data-testid="Credit Field"
                fullWidth
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ marginTop: "80px", border: "1px solid #0000004D" }} />

          {/* Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: "35px",
              gap: "30px",
            }}
          >
            <Button
              variant="outlined"
              sx={deleteButtonStyles}
              onClick={handleDelete}
              disabled={!entry}
            >
              Delete
            </Button>
            <Button
              variant="contained"
              sx={saveButtonStyles}
              onClick={handleSave}
            >
              Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </LocalizationProvider>
  );
};
